import React, { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import WeeklyCheckIn from './WeeklyCheckIn'
import SeverityScale from './SeverityScale'
import Progress from './Progress'
import Admin from './Admin'
import Education from './Education'
import { runCoachingEngine } from '../coaching'

export default function Dashboard({ session }) {
  const [profile, setProfile] = useState(null)
  const [latestScore, setLatestScore] = useState(null)
  const [todayCheckin, setTodayCheckin] = useState(null)
  const [coaching, setCoaching] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('dashboard')

  useEffect(() => {
    fetchData()
  }, [])

  async function fetchData() {
    setLoading(true)
    const uid = session.user.id

    const [{ data: profileData }, { data: scoreData }, { data: checkinData }, { data: coachingData }] =
      await Promise.all([
        supabase.from('profiles').select('*').eq('user_id', uid).single(),
        supabase.from('afib_scores').select('*').eq('user_id', uid).order('score_date', { ascending: false }).limit(1).single(),
        supabase.from('daily_checkins').select('*').eq('user_id', uid).eq('checkin_date', new Date().toISOString().split('T')[0]).single(),
        supabase.from('coaching_log').select('*').eq('user_id', uid).eq('log_date', new Date().toISOString().split('T')[0]).eq('physician_approved', true)
      ])

    setProfile(profileData)
    setLatestScore(scoreData)
    setTodayCheckin(checkinData)
    setCoaching(coachingData || [])
    setLoading(false)
  }

  async function handleSignOut() {
    await supabase.auth.signOut()
  }

  function calcChadsvasc() {
    if (!profile) return 0
    let score = 0
    if (profile.chf) score += 1
    if (profile.htn) score += 1
    if (profile.age >= 75) score += 2
    else if (profile.age >= 65) score += 1
    if (profile.diabetes) score += 1
    if (profile.prior_stroke_tia) score += 2
    if (profile.vascular_disease) score += 1
    if (profile.sex === 'female') score += 1
    return score
  }

  const categories = latestScore ? [
    { name: 'Anticoagulation', pts: latestScore.anticoag_pts, max: 25, included: latestScore.anticoag_included, color: '#185FA5' },
    { name: 'Medication compliance', pts: latestScore.medication_pts, max: 10, included: latestScore.medication_included, color: '#534AB7' },
    { name: 'Blood pressure', pts: latestScore.bp_pts, max: 13, included: latestScore.bp_included, color: latestScore.bp_included ? '#1D9E75' : '#888' },
    { name: 'Diabetes control', pts: latestScore.diabetes_pts, max: 10, included: latestScore.diabetes_included, color: latestScore.diabetes_included ? '#1D9E75' : '#888' },
    { name: 'Weight', pts: latestScore.weight_pts, max: 10, included: latestScore.weight_included, color: '#BA7517' },
    { name: 'Tobacco use', pts: latestScore.tobacco_pts, max: 7, included: true, color: '#1D9E75' },
    { name: 'Exercise', pts: latestScore.exercise_pts, max: 5, included: true, color: '#185FA5' },
    { name: 'Sleep hygiene', pts: latestScore.sleep_hrs_pts, max: 5, included: true, color: '#534AB7' },
    { name: 'Lipids', pts: latestScore.lipids_pts, max: 5, included: latestScore.lipids_included, color: '#BA7517' },
    { name: 'Alcohol use', pts: latestScore.alcohol_pts, max: 5, included: true, color: '#1D9E75' },
    { name: 'Caffeine', pts: latestScore.caffeine_pts, max: 5, included: true, color: '#1D9E75' },
  ] : []

  const tabs = ['dashboard', 'checkin', 'weekly', 'severity', 'progress', 'education', 'profile', 'admin']

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: 'Arial' }}>
      <p style={{ color: '#185FA5' }}>Loading your dashboard...</p>
    </div>
  )

  return (
    <div style={{ fontFamily: 'Arial', maxWidth: 480, margin: '0 auto', minHeight: '100vh', background: '#f0f4f8', paddingBottom: 80 }}>

      {/* Header */}
      <div style={{ background: '#185FA5', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ color: 'white', fontSize: 18, fontWeight: 700 }}>MyAFib Control</div>
          <div style={{ color: '#B5D4F4', fontSize: 12 }}>Heart Rhythm Center</div>
        </div>
        <button onClick={handleSignOut} style={{
          background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white',
          padding: '6px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer'
        }}>Sign out</button>
      </div>

      {/* Tab navigation */}
      <div style={{ display: 'flex', background: 'white', borderBottom: '1px solid #eee' }}>
        {tabs.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            flex: 1, padding: '12px 4px', border: 'none', background: 'none',
            fontSize: 11, cursor: 'pointer', textTransform: 'capitalize',
            color: activeTab === tab ? '#185FA5' : '#888',
            borderBottom: activeTab === tab ? '2px solid #185FA5' : '2px solid transparent',
            fontWeight: activeTab === tab ? 700 : 400
          }}>{tab}</button>
        ))}
      </div>

      <div style={{ padding: 16 }}>

        {/* DASHBOARD TAB */}
        {activeTab === 'dashboard' && (
          <div>
            {/* Score card */}
            <div style={{ background: 'white', borderRadius: 14, padding: 20, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 20 }}>
              <div style={{
                width: 90, height: 90, borderRadius: '50%', border: '3px solid #185FA5',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <div style={{ fontSize: 30, fontWeight: 700, color: '#185FA5', lineHeight: 1 }}>
                  {latestScore ? latestScore.scaled_score : '--'}
                </div>
                <div style={{ fontSize: 11, color: '#888' }}>/ 100</div>
              </div>
              <div>
               <div style={{ fontSize: 16, fontWeight: 700, color: '#2C2C2C', marginBottom: 4 }}>
                  AFib Control Score
                  {latestScore && !latestScore.weekly_completed && (
                    <span style={{ fontSize: 11, fontWeight: 400, color: '#185FA5', background: '#E6F1FB', padding: '2px 7px', borderRadius: 8, marginLeft: 8 }}>
                      Daily only
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#666', lineHeight: 1.5 }}>
                  {latestScore
                    ? latestScore.weekly_completed
                      ? `${latestScore.raw_points} pts earned out of ${latestScore.possible_points} possible`
                      : `${latestScore.raw_points} pts earned out of ${latestScore.possible_points} possible — complete weekly check-in for full score`
                    : 'Complete your first check-in to see your score'}
                </div>
                {!profile && (
                  <div style={{ fontSize: 12, color: '#185FA5', marginTop: 4, cursor: 'pointer', fontWeight: 600 }}
                    onClick={() => setActiveTab('profile')}>
                    Complete your profile first →
                  </div>
                )}
              </div>
            </div>

            {/* CHA2DS2-VASc */}
            <div style={{ background: '#E6F1FB', borderRadius: 12, padding: '12px 16px', marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0C447C' }}>CHA₂DS₂-VASc Score</div>
                  <div style={{ fontSize: 11, color: '#185FA5', marginTop: 2 }}>Calculated live from your profile</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#185FA5' }}>{calcChadsvasc()}</div>
                  <div style={{ fontSize: 10, color: '#185FA5' }}>stroke risk score</div>
                </div>
              </div>
              {profile?.afib_confirmed !== 'confirmed' && (
                <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: '#FAEEDA', fontSize: 11, color: '#633806', lineHeight: 1.6 }}>
                  ⚠️ The CHA₂DS₂-VASc score is used to estimate stroke risk in people with a confirmed AFib diagnosis. If you have not been formally diagnosed with AFib by a physician, please discuss your heart rhythm concerns with your healthcare provider before interpreting this score.
                </div>
              )}
            </div>

            {/* Today status */}
            <div style={{ background: 'white', borderRadius: 12, padding: '12px 16px', marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                Today's status
              </div>
              {todayCheckin ? (
                <div style={{ fontSize: 13, color: '#085041' }}>✅ Check-in complete for today</div>
              ) : (
                <div>
                  <div style={{ fontSize: 13, color: '#666', marginBottom: 10 }}>⏳ Daily check-in not yet completed</div>
                  <button onClick={() => setActiveTab('checkin')} style={{
                    background: '#185FA5', color: 'white', border: 'none', borderRadius: 8,
                    padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', width: '100%'
                  }}>
                    Start today's check-in
                  </button>
                </div>
              )}
            </div>

            {/* Category breakdown */}
            {latestScore && (
              <div style={{ background: 'white', borderRadius: 12, padding: '12px 16px', marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
                  Category breakdown
                </div>
                {categories.map(cat => (
                  <div key={cat.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <div style={{ fontSize: 12, color: '#333', width: 130, flexShrink: 0 }}>{cat.name}</div>
                    <div style={{ flex: 1, height: 6, background: '#eee', borderRadius: 3 }}>
                      {cat.included && cat.pts !== null && (
                        <div style={{ width: `${Math.round((cat.pts / cat.max) * 100)}%`, height: 6, background: cat.color, borderRadius: 3 }} />
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: '#888', width: 50, textAlign: 'right', flexShrink: 0 }}>
                      {cat.included && cat.pts !== null ? `${cat.pts}/${cat.max}` : 'N/A'}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Coaching messages */}
            {coaching.length > 0 && (
              <div style={{ background: 'white', borderRadius: 12, padding: '12px 16px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
                  Today's coaching
                </div>
                {coaching.map(msg => (
                  <div key={msg.id} style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: 12, marginBottom: 12 }}>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>{msg.category}</div>
                    <div style={{ fontSize: 13, color: '#333', lineHeight: 1.6 }}>{msg.personalized_message}</div>
                    <div style={{
                      display: 'inline-block', marginTop: 6, fontSize: 10, padding: '2px 8px', borderRadius: 8,
                      background: msg.priority === 'high' ? '#FCEBEB' : msg.priority === 'medium' ? '#FAEEDA' : '#E1F5EE',
                      color: msg.priority === 'high' ? '#A32D2D' : msg.priority === 'medium' ? '#633806' : '#085041'
                    }}>{msg.priority} priority</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* CHECK-IN TAB */}
        {activeTab === 'checkin' && (
          <CheckIn session={session} profile={profile} onComplete={() => { setActiveTab('dashboard'); fetchData() }} />
        )}

        {/* WEEKLY CHECK-IN TAB */}
        {activeTab === 'weekly' && (
          <WeeklyCheckIn
            session={session}
            profile={profile}
            onComplete={() => { setActiveTab('dashboard'); fetchData() }}
          />
        )}
{/* SEVERITY SCALE TAB */}
        {activeTab === 'severity' && (
          <SeverityScale
            session={session}
            onComplete={() => { setActiveTab('dashboard'); }}
          />
        )}
        
       {/* PROGRESS TAB */}
        {activeTab === 'progress' && (
          <Progress session={session} />
        )}

{/* EDUCATION TAB */}
        {activeTab === 'education' && (
          <Education session={session} profile={profile} />
        )}

        {/* PROFILE TAB */}
        {activeTab === 'profile' && (
          <ProfileForm session={session} profile={profile} onSave={() => fetchData()} />
        )}
        
        {/* ADMIN TAB */}
        {activeTab === 'admin' && (
          <Admin session={session} />
        )}

      </div>

      {/* Bottom nav */}
      <div style={{
        position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)',
        width: '100%', maxWidth: 480, background: 'white',
        borderTop: '1px solid #eee', padding: '8px 0 4px',
        flexDirection: 'column', display: 'flex'
      }}>
        <div style={{ display: 'flex' }}>
          {[['dashboard','🏠'],['checkin','✅'],['weekly','📋'],['severity','😊'],['progress','📈'],['education','📖'],['profile','👤'],['admin','⚙️']].map(([tab, icon]) => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              flex: 1, border: 'none', background: 'none', cursor: 'pointer',
              fontSize: 20, padding: '4px 0',
              opacity: activeTab === tab ? 1 : 0.4
            }}>{icon}</button>
          ))}
        </div>
        <div style={{
          textAlign: 'center', padding: '4px 0 2px', fontSize: 9,
          color: '#aaa'
        }}>
          © 2026 Jeffrey L. Williams, MD. All rights reserved.
        </div>
      </div>
    </div>
  )
}

// ── CHECK-IN COMPONENT ──────────────────────────────────────
function CheckIn({ session, profile, onComplete }) {
  const [form, setForm] = useState({
    exercise_minutes: 0, exercise_intensity: 'none',
    sleep_hours: 7, caffeine_level: 'none',
    alcohol_level: 'none', nicotine_used: false,
    anticoag_taken: null, anticoag_applicable: true,
    medication_taken: null, medication_applicable: false
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  function calcScore(f) {
    let exercise_pts = 0
    if (f.exercise_minutes >= 60) exercise_pts = 5
    else if (f.exercise_minutes >= 45) exercise_pts = 4
    else if (f.exercise_minutes >= 30) exercise_pts = 3
    else if (f.exercise_minutes >= 15) exercise_pts = 2
    else if (f.exercise_minutes >= 1) exercise_pts = 1

    let sleep_hrs_pts = 0
    if (f.sleep_hours >= 7 && f.sleep_hours <= 8) sleep_hrs_pts = 5
    else if (f.sleep_hours >= 6.5 && f.sleep_hours < 7) sleep_hrs_pts = 3
    else if (f.sleep_hours > 8 && f.sleep_hours <= 8.5) sleep_hrs_pts = 3
    else sleep_hrs_pts = 0

    const alcohol_pts = f.alcohol_level === 'none' ? 5 : f.alcohol_level === 'one' ? 3 : f.alcohol_level === 'two_three' ? 1 : 0
    const caffeine_pts = f.caffeine_level === 'none' || f.caffeine_level === 'one' ? 5 : f.caffeine_level === 'two_three' ? 3 : 0
    const anticoag_pts = !f.anticoag_applicable ? 0 : f.anticoag_taken ? 25 : 0
    const anticoag_included = f.anticoag_applicable

    const raw_points = exercise_pts + sleep_hrs_pts + alcohol_pts + caffeine_pts + anticoag_pts
    const possible_points = 5 + 5 + 5 + 5 + (anticoag_included ? 25 : 0)
    const scaled_score = Math.min(100, Math.round((raw_points / possible_points) * 100))

    return { exercise_pts, sleep_hrs_pts, alcohol_pts, caffeine_pts, anticoag_pts, anticoag_included, raw_points, possible_points, scaled_score }
  }

  async function handleSubmit() {
    setSaving(true)
    setError(null)
    const today = new Date().toISOString().split('T')[0]
    const score = calcScore(form)

    const { error: checkinError } = await supabase.from('daily_checkins').upsert({
      user_id: session.user.id, checkin_date: today, ...form
    }, { onConflict: 'user_id,checkin_date' })

    if (!checkinError) {
      await supabase.from('afib_scores').upsert({
        user_id: session.user.id, score_date: today,
        exercise_pts: score.exercise_pts,
        sleep_hrs_pts: score.sleep_hrs_pts,
        alcohol_pts: score.alcohol_pts,
        caffeine_pts: score.caffeine_pts,
        anticoag_pts: score.anticoag_pts,
        anticoag_included: score.anticoag_included,
        tobacco_pts: 0,
        lipids_pts: 0,
        bp_pts: 0,
        bp_included: false,
        weight_pts: 0,
        weight_included: false,
        diabetes_pts: 0,
        diabetes_included: false,
        raw_points: score.raw_points,
        possible_points: score.possible_points,
        scaled_score: score.scaled_score,
        weekly_completed: false
      }, { onConflict: 'user_id,score_date' })
    
      await runCoachingEngine(
          session.user.id,
          profile,
          null,
          { ...form },
          null
        )
        onComplete()
    } else {
      setError(checkinError.message)
    }
    setSaving(false)
  }

  const btnStyle = (active) => ({
    padding: '8px 14px', borderRadius: 20, border: '1px solid #ddd', fontSize: 12,
    cursor: 'pointer', background: active ? '#185FA5' : 'white',
    color: active ? 'white' : '#555', fontWeight: active ? 700 : 400
  })

  return (
    <div>
      <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: '#2C2C2C' }}>🏃 Exercise today</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <input type="range" min="0" max="120" value={form.exercise_minutes}
            onChange={e => setForm({ ...form, exercise_minutes: parseInt(e.target.value) })}
            style={{ flex: 1 }} />
          <span style={{ fontSize: 14, fontWeight: 700, color: '#185FA5', minWidth: 50 }}>{form.exercise_minutes} min</span>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {['none','light','moderate','vigorous'].map(i => (
            <button key={i} style={btnStyle(form.exercise_intensity === i)}
              onClick={() => setForm({ ...form, exercise_intensity: i })}>{i}</button>
          ))}
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: '#2C2C2C' }}>😴 Sleep last night</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <input type="range" min="3" max="12" step="0.5" value={form.sleep_hours}
            onChange={e => setForm({ ...form, sleep_hours: parseFloat(e.target.value) })}
            style={{ flex: 1 }} />
          <span style={{ fontSize: 14, fontWeight: 700, color: '#185FA5', minWidth: 36 }}>{form.sleep_hours}h</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#888', marginTop: 4 }}>
          <span>3h</span><span>Goal: 7–8h</span><span>12h</span>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: '#2C2C2C' }}>☕ Caffeine today</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[['none','None'],['one','1 drink'],['two_three','2–3 drinks'],['four_plus','4+ drinks']].map(([val, label]) => (
            <button key={val} style={btnStyle(form.caffeine_level === val)}
              onClick={() => setForm({ ...form, caffeine_level: val })}>{label}</button>
          ))}
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: '#2C2C2C' }}>🍷 Alcohol today</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[['none','None'],['one','1 drink'],['two_three','2–3 drinks'],['binge','4+ (binge)']].map(([val, label]) => (
            <button key={val} style={btnStyle(form.alcohol_level === val)}
              onClick={() => setForm({ ...form, alcohol_level: val })}>{label}</button>
          ))}
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: '#2C2C2C' }}>🚬 Nicotine today</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {[[false,'No'],[true,'Yes']].map(([val, label]) => (
            <button key={label} style={btnStyle(form.nicotine_used === val)}
              onClick={() => setForm({ ...form, nicotine_used: val })}>{label}</button>
          ))}
        </div>
      </div>

     <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: '#2C2C2C' }}>💊 Anticoagulation taken?</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          {[[true, true,'Yes ✅'],[false, true,'No ❌'],[null, false,'Not prescribed']].map(([val, applicable, label]) => (
            <button key={label}
              style={btnStyle(form.anticoag_taken === val && form.anticoag_applicable === applicable)}
              onClick={() => setForm({ ...form, anticoag_taken: val, anticoag_applicable: applicable })}>{label}</button>
          ))}
        </div>
        {form.anticoag_taken === false && (
          <div style={{ background: '#FCEBEB', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#A32D2D', lineHeight: 1.6 }}>
            ⚠️ Missing your blood thinner significantly increases your risk of stroke. AFib increases stroke risk 4-fold, and 70% of first strokes from AFib are fatal or disabling. Please take your anticoagulation medication as soon as possible. If you have concerns about your medication, contact your provider — do not stop taking it without medical guidance.
          </div>
        )}
      </div>

{(profile?.htn || profile?.diabetes || profile?.hyperlipidemia) && (
          <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: '#2C2C2C' }}>💊 Cardiac medications taken today?</div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 10 }}>
              {[profile?.htn && 'blood pressure', profile?.diabetes && 'diabetes', profile?.hyperlipidemia && 'cholesterol'].filter(Boolean).join(', ')} medication
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
              {[[true, true,'Yes ✅'],[false, true,'No ❌']].map(([val, applicable, label]) => (
                <button key={label}
                  style={btnStyle(form.medication_taken === val)}
                  onClick={() => setForm({ ...form, medication_taken: val, medication_applicable: true })}>{label}</button>
              ))}
            </div>
            {form.medication_taken === false && (
              <div style={{ background: '#FCEBEB', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#A32D2D', lineHeight: 1.6 }}>
                ⚠️ Missing cardiac medications increases your risk of AFib episodes and stroke. Your {[profile?.htn && 'blood pressure', profile?.diabetes && 'diabetes', profile?.hyperlipidemia && 'cholesterol'].filter(Boolean).join(', ')} medication is an important part of your AFib management. Please take it as soon as possible and contact your provider if you have concerns.
              </div>
            )}
          </div>
        )}
      {error && <div style={{ color: '#A32D2D', fontSize: 13, marginBottom: 10, padding: '8px 12px', background: '#FCEBEB', borderRadius: 8 }}>{error}</div>}

      <button onClick={handleSubmit} disabled={saving} style={{
        width: '100%', padding: 14, background: saving ? '#9CB8D9' : '#185FA5',
        color: 'white', border: 'none', borderRadius: 10, fontSize: 15,
        fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer'
      }}>
        {saving ? 'Saving...' : 'Save today\'s check-in'}
      </button>
    </div>
  )
}

// ── PROFILE FORM COMPONENT ───────────────────────────────────
function ProfileForm({ session, profile, onSave }) {
  const [form, setForm] = useState({
    age: '', sex: 'male', height_in: '', weight_lbs_baseline: '',
    has_bp_cuff: false, has_sleep_apnea: false, uses_cpap: false,
    htn: false, diabetes: false, hyperlipidemia: false, chf: false,
    vascular_disease: false, prior_stroke_tia: false, alcoholism: false, nicotine_use: false,
    anticoag_prescribed: false, anticoag_medication: '', anticoag_discussed: false,
    goal_timeframe: 'long_term', care_approach: 'moderate', disclosure_accepted: false
  })
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (profile) setForm({ ...form, ...profile })
  }, [profile])

  async function handleSave() {
    setSaving(true)
    const bmi = form.height_in && form.weight_lbs_baseline
      ? parseFloat(((form.weight_lbs_baseline / (form.height_in * form.height_in)) * 703).toFixed(1))
      : null

    await supabase.from('profiles').upsert({
      user_id: session.user.id, ...form,
      age: parseInt(form.age),
      height_in: parseFloat(form.height_in),
      weight_lbs_baseline: parseFloat(form.weight_lbs_baseline),
      bmi_baseline: bmi,
      updated_at: new Date().toISOString()
    })
    setSaving(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
    onSave()
  }

  const field = (label, key, type = 'text') => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>{label}</label>
      <input type={type} value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })}
        style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' }} />
    </div>
  )

  const check = (label, key) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 13, color: '#333', cursor: 'pointer' }}>
      <input type="checkbox" checked={form[key]} onChange={e => setForm({ ...form, [key]: e.target.checked })}
        style={{ width: 16, height: 16 }} />
      {label}
    </label>
  )

  const bmi = form.height_in && form.weight_lbs_baseline
    ? ((form.weight_lbs_baseline / (form.height_in * form.height_in)) * 703).toFixed(1) : '--'

  return (
    <div>
      <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14, color: '#2C2C2C' }}>Demographics</div>
        {field('Age', 'age', 'number')}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>Sex</label>
          <select value={form.sex} onChange={e => setForm({ ...form, sex: e.target.value })}
            style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14 }}>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>
        {field('Height (inches)', 'height_in', 'number')}
        {field('Weight (lbs)', 'weight_lbs_baseline', 'number')}
        <div style={{ background: '#E6F1FB', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: '#0C447C' }}>
          BMI (auto-calculated): <strong>{bmi}</strong>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14, color: '#2C2C2C' }}>Medical history</div>
        {check('Hypertension', 'htn')}
        {check('Diabetes', 'diabetes')}
        {check('Hyperlipidemia', 'hyperlipidemia')}
        {check('Sleep apnea', 'has_sleep_apnea')}
        {form.has_sleep_apnea && check('Uses CPAP', 'uses_cpap')}
        {check('Congestive heart failure (CHF)', 'chf')}
        {check('Vascular disease', 'vascular_disease')}
        {check('Prior stroke or TIA', 'prior_stroke_tia')}
        {check('Alcoholism', 'alcoholism')}
        {check('Nicotine use', 'nicotine_use')}
        {check('Owns a blood pressure cuff', 'has_bp_cuff')}
      </div>

      <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14, color: '#2C2C2C' }}>Anticoagulation</div>
        {check('Prescribed anticoagulation', 'anticoag_prescribed')}
        {form.anticoag_prescribed && field('Medication name (e.g. apixaban)', 'anticoag_medication')}
        {check('Discussed stroke risk with provider', 'anticoag_discussed')}
      </div>

      <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14, color: '#2C2C2C' }}>Goals</div>
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>Goal timeframe</label>
          <select value={form.goal_timeframe} onChange={e => setForm({ ...form, goal_timeframe: e.target.value })}
            style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14 }}>
            <option value="short_term">Short term (1–2 months)</option>
            <option value="long_term">Long term (6–12 months)</option>
          </select>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>Care approach</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { value: 'lifestyle_first', label: 'Lifestyle first', desc: 'I prefer to manage my AFib through lifestyle changes before considering medications or procedures.' },
              { value: 'open_to_medications', label: 'Open to medications', desc: 'I am willing to take medications to control my heart rate or rhythm if lifestyle alone is not enough.' },
              { value: 'rhythm_control', label: 'Rhythm control', desc: 'I am interested in antiarrhythmic medications and/or cardioversion to maintain normal rhythm.' },
              { value: 'open_to_ablation', label: 'Open to ablation', desc: 'I am willing to consider catheter ablation or other invasive procedures for long-term rhythm control.' },
            ].map(option => (
              <div key={option.value}
                onClick={() => setForm({ ...form, care_approach: option.value })}
                style={{
                  padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                  border: form.care_approach === option.value ? '2px solid #185FA5' : '1px solid #ddd',
                  background: form.care_approach === option.value ? '#E6F1FB' : 'white'
                }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: form.care_approach === option.value ? '#185FA5' : '#2C2C2C', marginBottom: 4 }}>
                  {form.care_approach === option.value ? '✓ ' : ''}{option.label}
                </div>
                <div style={{ fontSize: 12, color: '#666', lineHeight: 1.5 }}>
                  {option.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
        {check('I understand this is an educational tool, not medical advice', 'disclosure_accepted')}
      </div>

      {saved && (
        <div style={{ background: '#E1F5EE', color: '#085041', padding: '10px 14px', borderRadius: 8, marginBottom: 10, fontSize: 13, fontWeight: 600 }}>
          ✅ Profile saved successfully!
        </div>
      )}

      <button onClick={handleSave} disabled={saving} style={{
        width: '100%', padding: 14, background: saving ? '#9CB8D9' : '#185FA5',
        color: 'white', border: 'none', borderRadius: 10, fontSize: 15,
        fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer'
      }}>
        {saving ? 'Saving...' : 'Save profile'}
      </button>
    
    </div>
  )
}